import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import GatherTile from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/GatherTile';
import EnterTextSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/EnterTextSheet';
import RecordInterviewSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/RecordInterviewSheet';
import { openImportDocumentSidebar } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Signals/importDocumentSidebarSignal';
import { openAddFromDossierSheet } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Signals/addFromDossierSheetSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import { FileUp, FolderOpen, Keyboard, Mic, NotebookPen } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

// Four or five tiles fall on rows of two: more columns would squeeze each title in a 1050px layout.
const gridStyle = (columns: number) => css`
	display: grid;
	grid-template-columns: repeat(${columns > 3 ? 2 : columns}, minmax(0, 1fr));
	gap: 0.625rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
};

/**
 * The entry points that feed an open preparation: record or import an interview, dictate a note, enter a
 * text, import a Word or PDF document, pick from the dossier. Recording and dictating go through the
 * capture screens, which carry the preparation in `?preparation=`; the audio import and the text entry
 * happen in their sheets and the document import in the shared panel. A button the group does not accept is not rendered.
 */
const GatherSection: React.FC<Props> = ({ subject, preparation }) => {
	const navigate = useNavigate();
	const canCapture = useHasPermission(PermissionEnum.INTERVIEW_WRITE);
	const canImport = useHasPermission(
		[PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH],
		'any',
	);
	const [isRecordSheetVisible, setIsRecordSheetVisible] = React.useState(false);
	const [isTextSheetVisible, setIsTextSheetVisible] = React.useState(false);

	const capturePath = `${buildSubjectPath(subject)}/interviews/new`;
	const preparationQuery = `?preparation=${preparation.id}`;

	const handleOpenRecordSheet = React.useCallback(() => {
		setIsRecordSheetVisible(true);
	}, []);

	const handleCloseRecordSheet = React.useCallback(() => {
		setIsRecordSheetVisible(false);
	}, []);

	const handleOpenTextSheet = React.useCallback(() => {
		setIsTextSheetVisible(true);
	}, []);

	const handleCloseTextSheet = React.useCallback(() => {
		setIsTextSheetVisible(false);
	}, []);

	const handleRecordClick = React.useCallback(() => {
		navigate(`${capturePath}/interview${preparationQuery}`);
	}, [navigate, capturePath, preparationQuery]);

	const handleNoteClick = React.useCallback(() => {
		navigate(`${capturePath}/notes${preparationQuery}`);
	}, [navigate, capturePath, preparationQuery]);

	const handleImportDocumentClick = React.useCallback(() => {
		openImportDocumentSidebar(preparation.id);
	}, [preparation.id]);

	const handleAddFromDossierClick = React.useCallback(() => {
		openAddFromDossierSheet(
			preparation.acceptsInterviews || preparation.acceptsNotes ? 'interviews' : 'documents',
		);
	}, [preparation.acceptsInterviews, preparation.acceptsNotes]);

	const showInterview = preparation.acceptsInterviews && canCapture;
	const showNote = preparation.acceptsNotes && canCapture;
	const showText = preparation.acceptsNotes && canCapture;
	const showImportDocument = preparation.acceptsDocuments && canImport;
	const showFromDossier =
		preparation.acceptsInterviews || preparation.acceptsNotes || preparation.acceptsDocuments;
	const columns = [showInterview, showNote, showText, showImportDocument, showFromDossier].filter(
		Boolean,
	).length;

	if (columns === 0) {
		return null;
	}

	return (
		<div css={gridStyle(columns)}>
			<ConditionalLoader data={showInterview}>
				<GatherTile
					icon={<Mic size={20} aria-hidden="true" />}
					accent={theme.colors.accent}
					title="Enregistrer un entretien"
					subtitle="Maintenant, ou depuis un fichier audio"
					onClick={handleOpenRecordSheet}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={showNote}>
				<GatherTile
					icon={<NotebookPen size={20} aria-hidden="true" />}
					accent={theme.colors.validated}
					title="Dicter une note"
					subtitle="Transcrite, puis ajoutée"
					onClick={handleNoteClick}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={showText}>
				<GatherTile
					icon={<Keyboard size={20} aria-hidden="true" />}
					accent={theme.colors.validated}
					title="Saisir un texte"
					subtitle="Tapé ou collé, puis ajouté"
					onClick={handleOpenTextSheet}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={showImportDocument}>
				<GatherTile
					icon={<FileUp size={20} aria-hidden="true" />}
					accent={theme.tints.raspberry.solid}
					title="Importer un document"
					subtitle="Word ou PDF, ajouté à la préparation"
					onClick={handleImportDocumentClick}
				/>
			</ConditionalLoader>
			<ConditionalLoader data={showFromDossier}>
				<GatherTile
					icon={<FolderOpen size={20} aria-hidden="true" />}
					accent={theme.tints.amber.solid}
					title="Ajouter depuis le dossier"
					subtitle="Entretiens, notes et documents déjà là"
					onClick={handleAddFromDossierClick}
				/>
			</ConditionalLoader>
			<EnterTextSheet
				subject={subject}
				preparationId={preparation.id}
				visible={isTextSheetVisible}
				onHide={handleCloseTextSheet}
			/>
			<RecordInterviewSheet
				subject={subject}
				preparationId={preparation.id}
				visible={isRecordSheetVisible}
				onHide={handleCloseRecordSheet}
				onRecord={handleRecordClick}
			/>
		</div>
	);
};

export default GatherSection;
